import React, { useState, useMemo, useRef } from "react";
import { Lock, PackagePlus, Banknote, Users, AtSign } from "lucide-react";
import { LUNCH_COLOR, SLOT_MIN, DAY_OFF_MODES, colorForAppt, OFF_DUTY_APPT_COLOR, buildGroupPalette } from "./constants";
import { baliMinutes, baliLocalTime, parseHM, pad } from "./timeUtils";
import { computeLanes } from "./overlapLanes";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { makeLongPressState, longPressHandlers, consumedLongPress } from "./touchLongPress";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";

// Format an appointment's Bali-local end time given its start ISO and
// duration. Matches the `HH:MM` convention `baliLocalTime` uses so the
// two ends of the range on every tile stay visually consistent.
function endLocalTime(startIso, durationMin) {
  const startMin = baliMinutes(startIso);
  const endMin = startMin + (durationMin || 30);
  const hh = Math.floor(endMin / 60) % 24;
  const mm = endMin % 60;
  return `${pad(hh)}:${pad(mm)}`;
}

// The day-view time grid.
//
// Renders:
//   • a sticky header row with the visible staff columns (clicking a name
//     enters the per-staff week view via `onEnterWeekStaff`)
//   • a body of 15-minute slot rows
//   • grey-net overlays on staff columns that can't perform the currently-
//     dragged service (so operators get a visual "no" instead of a silent
//     error toast at drop time)
//   • lunch blocks (draggable within the same staff column, right-click to
//     delete) and appointment blocks (draggable across cells, resizable at
//     the bottom edge, right-click for the context menu)
//
// All state lives in the parent BranchCalendar; this component is a big
// controlled render. Handlers are hoisted so drop math can share the grid
// and body refs.
export default function DayGrid({
  // Layout
  staffForCalendar, slots, colMinW, slotPx,
  // Data
  appts, resizePreview,
  // Packages catalog — used to resolve `package_id` → package name for the
  // hover tooltip on linked (package) appointments.
  packages = [],
  // Iter-278 · Services catalog is now consulted on every tile so the
  // zebra buffer + tile height can back-fill from the service's
  // `processing_time_min` when a legacy appointment record still
  // carries a 0 (i.e. was booked before the buffer feature landed).
  services = [],
  // Weekday key so we can read schedule + lunch info off each staff doc
  wKey,
  // The calendar day being rendered (JS Date). Used to compute the
  // per-tile instance ID (`break_{staffId}_{YYYY-MM-DD}`) so each
  // lunch tile has a UNIQUE, date-scoped handle for the drag-drop
  // instance-targeting fix (Feb 2026).
  date,
  // Drag state (owned by parent)
  dragActive, setDragActive, staffCanPerformService,
  // Refs for drop-target math (parent owns them)
  gridRef, bodyRef,
  // Handlers
  onEnterWeekStaff, onCellClick, onGridDragOver, onGridDrop,
  onLunchDragStart, onLunchDelete,
  onApptClick, onApptContextMenu, onApptResizeStart,
  // Iter-380 · Missing-Assistant alert. Fires when the operator clicks
  // the amber "⚠ Assign Assistant" banner on a Requires Assistant tile
  // that still has no assistant assigned. Also fires on a plain tile
  // click for the same subset of tiles, so a single tap on the gap
  // goes straight to the Edit form scrolled to the staff section.
  onApptAssignAssistant,
  // Predicate: is this staff available at this minute? (schedule + lunch aware)
  staffAvailableAt,
  // When true, appointment tiles switch to tap-friendly targets and left
  // clicks open the Personal View menu (AI Summary / Medical Record)
  // instead of the double-book cell click.
  personalMode = false,
  // Live-sync presence (Feb 2026): perforated dashed squares showing
  // where another operator is drafting a new appointment. Array of
  // `{ key, branch_id, staff_id, start_iso, end_iso, by_id, by_name,
  //   connection_id, at }`.
  remotePresence = [],
  openMin = 0,
}) {
  // Booking Calendar · Edit gate — the same permission that governs the
  // "Edit" item in the appointment context menu also decides whether
  // block-outs (lunch breaks) are draggable. Without the tick the lunch
  // block still renders (so operators can see the schedule) but doesn't
  // accept a drag, and right-click / long-press swallow the delete.
  const { staff: currentStaff } = useAuth();
  const canEditCalendar = hasPermission(currentStaff, "location.booking_calendar.edit");
  // Iter-278 — Legacy zebra back-fill map. Every tile looks up its
  // service definition here so an appointment whose own
  // `processing_time_min` is 0 (booked before the buffer feature
  // shipped) can still render the diagonal-striped tail (and
  // grow the tile height) from the service's own value.
  const serviceById = useMemo(() => {
    const m = new Map();
    for (const s of services || []) m.set(s.id, s);
    return m;
  }, [services]);
  // Instance date — ISO (YYYY-MM-DD) form of the calendar day being
  // rendered. Stamped onto every lunch tile so each break has a
  // UNIQUE per-day handle and the parent's onDrop knows exactly
  // which instance to PATCH.
  const dateIso = date ? (date instanceof Date ? date.toISOString().slice(0, 10) : String(date).slice(0, 10)) : "";
  // Shared long-press state for every tile + lunch block in this grid.
  // One finger is on-screen at a time so a single state cursor is enough.
  const lpState = useRef(makeLongPressState()).current;
  const columns = `56px repeat(${Math.max(1, staffForCalendar.length)}, minmax(${colMinW}px, 1fr))`;
  // Hovering ANY appointment tile that has a `link_group_id` lights up every
  // sibling tile with the same id (a package spans 2..N appointments across
  // rooms/staff/time). Local to this grid — cleared on mouseleave.
  const [hoveredLinkId, setHoveredLinkId] = useState(null);
  // Group-booking hover state.
  //
  // Two shapes co-exist per spec:
  //   • hoveredGroupScope  — set when the pointer is over a tile that
  //     belongs to an appointment group (`group_id`). Carries the group
  //     colour AND the full set of appointment ids that must glow —
  //     including package (`link_group_id`) siblings of ANY group
  //     member, so a group of 3 clients each on a 2-appointment package
  //     lights all 6 tiles in the same group colour.
  //   • hoveredLinkId — legacy per-package hover (amber ring). Only
  //     used when the hovered tile is part of a package chain but NOT
  //     part of a group. Group hover ALWAYS wins over link hover.
  const [hoveredGroupScope, setHoveredGroupScope] = useState(null);

  // Stable group_id → palette map for the appointments currently rendered.
  const groupPalette = useMemo(() => buildGroupPalette(appts), [appts]);

  // Fast lookups by group_id and link_group_id — rebuilt only when the
  // appointment list changes, not on every hover, so glow computation is
  // O(size of group + size of package) per enter.
  const apptIndex = useMemo(() => {
    const byGroup = new Map();
    const byLink = new Map();
    for (const a of appts) {
      if (a.group_id) {
        if (!byGroup.has(a.group_id)) byGroup.set(a.group_id, []);
        byGroup.get(a.group_id).push(a);
      }
      if (a.link_group_id) {
        if (!byLink.has(a.link_group_id)) byLink.set(a.link_group_id, []);
        byLink.get(a.link_group_id).push(a);
      }
    }
    return { byGroup, byLink };
  }, [appts]);

  const enterGroup = (appt) => {
    if (!appt || !appt.group_id) return;
    const set = new Set();
    const siblings = apptIndex.byGroup.get(appt.group_id) || [];
    for (const s of siblings) {
      set.add(s.id);
      // Extend to every appointment in that member's package chain, so
      // a group + package combo glows the whole package in group colour.
      if (s.link_group_id) {
        const chain = apptIndex.byLink.get(s.link_group_id) || [];
        for (const l of chain) set.add(l.id);
      }
    }
    setHoveredGroupScope({ groupId: appt.group_id, ids: set });
  };
  const leaveGroup = (gid) => {
    setHoveredGroupScope((prev) => (prev && prev.groupId === gid ? null : prev));
  };

  // Double-booked tiles split the column side-by-side (Mindbody style).
  // Per staff column: assign each appointment a lane within its overlap
  // cluster so concurrent bookings render half/third width next to each other.
  // Iter-379 · Requires Assistant — the assistant's column is only held
  // for `processing_time_min` minutes (not the full service_time), so
  // lane computation must shorten the effective duration when painting
  // an assist tile, otherwise a 15-min assist block would appear to
  // reserve the same column for 90 min and starve the next booking.
  const laneLayouts = useMemo(() => {
    const map = {};
    for (const s of staffForCalendar) {
      const list = appts.filter((a) =>
        (a.staff_id === s.id || (Array.isArray(a.assist_staff_ids) && a.assist_staff_ids.includes(s.id)))
        && a.status !== "cancelled");
      map[s.id] = computeLanes(
        list,
        (a) => baliMinutes(a.start_iso),
        (a) => {
          // Iter-387 · Honour the service's current Requires Assistant
          // flag when the appointment snapshot is stale (same fallback
          // the tile renderer uses) so lane packing agrees with the
          // visible tile height.
          const svcDef = serviceById.get(a.service_id);
          const effRequiresAssistant = !!(a.requires_assistant || svcDef?.requires_assistant);
          if (effRequiresAssistant && s.id !== a.staff_id) {
            // Assist column for a Requires Assistant service → the
            // effective calendar hold is strictly the processing time.
            const svcProcMin = Math.max(0, Number(svcDef?.processing_time_min || 0));
            const apptProcMin = Math.max(0, Number(a.processing_time_min || 0));
            return Math.max(5, Math.max(apptProcMin, svcProcMin));
          }
          return resizePreview[a.id] || a.duration_min || 30;
        },
      );
    }
    return map;
  }, [staffForCalendar, appts, resizePreview, serviceById]);

  return (
    <div className="min-w-full" ref={gridRef} onDragOver={onGridDragOver} onDrop={onGridDrop}>
      {/* Header row */}
      <div
        className="grid sticky top-0 bg-white z-10 border-b border-border"
        style={{ gridTemplateColumns: columns }}
      >
        <div className="p-2 text-[10px] tracking-widest text-muted-foreground">TIME</div>
        {staffForCalendar.map((s) => {
          const canDo = !dragActive || !dragActive.service_id || staffCanPerformService(s, dragActive.service_id);
          const isInactive = s.active === false;
          const isDayOff = !isInactive && DAY_OFF_MODES.has((((s.schedule || {})[wKey]) || {}).mode);
          return (
            <button
              type="button"
              key={s.id}
              onClick={() => onEnterWeekStaff(s.id)}
              className={"p-2 text-xs font-medium border-l border-border truncate text-left hover:bg-muted/60 transition-colors " + (canDo && !isInactive && !isDayOff ? "" : "text-muted-foreground")}
              data-testid={`staff-col-header-${s.id}`}
              title={`See ${s.full_name}'s full week`}
            >
              {s.full_name}
              {isInactive && <span className="ml-1 text-[9px] uppercase tracking-wider" data-testid={`staff-inactive-tag-${s.id}`}>· inactive</span>}
              {isDayOff && <span className="ml-1 text-[9px] uppercase tracking-wider" data-testid={`staff-dayoff-tag-${s.id}`}>· day off</span>}
              {!canDo && <span className="ml-1 text-[9px] uppercase tracking-wider">· not trained</span>}
            </button>
          );
        })}
        {staffForCalendar.length === 0 && <div className="p-2 text-xs text-muted-foreground border-l">No staff assigned</div>}
      </div>

      {/* Body: slot rows + optional grey-net overlays per incompatible staff */}
      <div className="relative" ref={bodyRef}>
        {/* Grey-net overlays: rendered once per incompatible staff column while
            a drag is in flight. They're pointer-events-none so drops still hit
            the underlying grid; the operator only sees a visual hint that this
            column won't accept the appointment. */}
        {dragActive && dragActive.service_id && staffForCalendar.map((s, i) => {
          const canDo = staffCanPerformService(s, dragActive.service_id);
          if (canDo) return null;
          const colW = `calc((100% - 56px) / ${Math.max(1, staffForCalendar.length)})`;
          return (
            <div
              key={"greynet-" + s.id}
              className="absolute top-0 bottom-0 pointer-events-none z-20"
              data-testid={`greynet-${s.id}`}
              style={{
                left: `calc(56px + ${i} * ${colW})`,
                width: colW,
                backgroundImage: "repeating-linear-gradient(45deg, rgba(120,120,120,0.28) 0 6px, rgba(255,255,255,0) 6px 12px)",
                backgroundColor: "rgba(120,120,120,0.14)",
              }}
            />
          );
        })}

        {slots.map((minute) => {
          const hh = Math.floor(minute / 60);
          const mm = minute % 60;
          // Iter-225 — Time-column labels now include :30 half-hour ticks
          // alongside the primary :00 hours. Half-hour labels render in
          // a lighter opacity so the hour markers remain dominant.
          const isHourMark = mm === 0;
          const isHalfHourMark = mm === 30;
          const label = isHourMark
            ? `${pad(hh)}:00`
            : (isHalfHourMark ? `${pad(hh)}:30` : "");
          return (
            <div
              key={minute}
              // Iter-225 — dark hour divider now sits ABOVE the ':00'
              // row (border-t, not border-b) so operators read "the
              // dark line right BEFORE 10:00 marks the top of the
              // 10:00 hour". Sub-hour rows keep a light top border.
              className={"grid border-t " + (isHourMark ? "border-border" : "border-border/30")}
              style={{ gridTemplateColumns: columns, height: slotPx }}
            >
              <div
                className="text-[10px] font-medium text-black px-2 pt-0.5"
                data-testid={`time-label-${pad(hh)}${pad(mm)}`}
              >
                {label}
              </div>
              {staffForCalendar.map((s) => {
                const available = staffAvailableAt(s, minute);
                // Show an appointment in this staff column when they are the
                // primary staff OR listed as a co-practitioner via
                // `assist_staff_ids` (services with required_staff > 1).
                // Appointments anchor at the 15-min slot whose range
                // *contains* their start minute — so a tile that starts at
                // 10:50 renders inside the 10:45 slot, offset by 5 min of
                // pixels from the top (see `top` in the tile style below).
                // This is what lets package siblings whose start times land
                // on the odd 10:50 / 11:20 / 12:05 offsets (because the
                // previous sibling's duration didn't fall on a 15-min
                // boundary) actually paint on the grid.
                const cellAppts = appts.filter((a) => {
                  if (!(a.staff_id === s.id || (Array.isArray(a.assist_staff_ids) && a.assist_staff_ids.includes(s.id)))) return false;
                  if (a.status === "cancelled") return false;
                  const bm = baliMinutes(a.start_iso);
                  return bm >= minute && bm < minute + SLOT_MIN;
                });
                const daySchedule = (s.schedule || {})[wKey] || {};
                const ls = parseHM(daySchedule.lunch_start, null);
                const le = parseHM(daySchedule.lunch_end, null);
                const dayIsOff = DAY_OFF_MODES.has(daySchedule.mode);
                // Off-duty or deactivated practitioner: tiles render grey so
                // the operator drags them to an available column.
                const colIsOff = dayIsOff || s.active === false;
                const isLunchAnchor = !dayIsOff && s.active !== false && ls != null && le != null && ls === minute;
                // Live-sync presence (Feb 2026): perforated dashed square
                // shown when a peer operator is drafting a new booking on
                // this exact staff column + slot. Filtered to entries
                // whose Bali-local start minute falls inside the current
                // 15-min row.
                const presenceInCell = (remotePresence || []).filter((p) => {
                  if (p.staff_id !== s.id) return false;
                  try {
                    const bm = baliMinutes(p.start_iso);
                    return bm >= minute && bm < minute + SLOT_MIN;
                  } catch { return false; }
                });
                return (
                  <button
                    key={s.id + "-" + minute}
                    onClick={(e) => {
                      if (!available) { e.preventDefault(); return; }
                      onCellClick(s.id, minute);
                    }}
                    className={
                      "relative border-l border-border/60 text-left transition-colors " +
                      (available ? "hover:bg-muted/50" : "bg-secondary/60 cursor-not-allowed")
                    }
                    data-testid={`cell-${s.id}-${minute}`}
                    // NEVER use the native `disabled` attribute here. A
                    // disabled <button> swallows pointer events for its
                    // ENTIRE subtree in Chrome/Safari, which killed
                    // drag-and-drop for every appointment tile sitting in
                    // an unavailable cell — exactly the day-off (grey)
                    // column case: the operator could not drag those
                    // bookings onto another practitioner. The cell's own
                    // click is already gated by `available` below, so the
                    // behaviour is identical minus the drag block.
                    aria-disabled={!available && !isLunchAnchor}
                  >
                    {isLunchAnchor && (                      <div
                        draggable={canEditCalendar}
                        onDragStart={(e) => {
                          if (!canEditCalendar) { e.preventDefault(); return; }
                          e.stopPropagation();
                          e.dataTransfer.setData("text/lunch", `${s.id}|${ls}`);
                          // Top-edge anchoring (Feb 2026):
                          // Capture the pointer-to-tile-top offset so the
                          // parent's `computeDropTarget` can subtract it
                          // and snap the drop to the TOP of the tile
                          // rather than wherever the finger/mouse cursor
                          // sits inside the tile. On iPad the finger
                          // typically presses in the middle of a 60-min
                          // lunch block — without this offset the drop
                          // lands 2-3 slots below the visual top edge.
                          const rect = e.currentTarget.getBoundingClientRect();
                          const offsetY = e.clientY - rect.top;
                          e.dataTransfer.setData(
                            "text/tile-offset-y",
                            String(Number.isFinite(offsetY) ? offsetY : 0),
                          );
                          // Solid-colour drag preview: clone the tile so
                          // the drag ghost is a full-opacity duplicate
                          // that visually tracks the finger. Freeze the
                          // clone's width/height so the `left-1 right-1`
                          // rule on the source doesn't stretch it across
                          // the whole viewport once it's detached from
                          // its positioned parent (same fix pattern as
                          // the appointment drag preview below).
                          try {
                            const clone = e.currentTarget.cloneNode(true);
                            clone.style.position = "fixed";
                            clone.style.left = "-1000px";
                            clone.style.top = "-1000px";
                            clone.style.right = "auto";
                            clone.style.width = `${rect.width}px`;
                            clone.style.height = `${rect.height}px`;
                            clone.style.opacity = "1";
                            clone.style.pointerEvents = "none";
                            document.body.appendChild(clone);
                            // Anchor the drag image at (offsetX, offsetY)
                            // — the exact pixel INSIDE the tile the user
                            // grabbed — so the ghost stays glued to the
                            // finger without visually jumping. The drop
                            // math still uses the tile's TOP edge because
                            // we pass offsetY on the DataTransfer above.
                            const offsetX = e.clientX - rect.left;
                            e.dataTransfer.setDragImage(
                              clone,
                              Number.isFinite(offsetX) ? offsetX : 20,
                              Number.isFinite(offsetY) ? offsetY : 10,
                            );
                            setTimeout(() => {
                              try { document.body.removeChild(clone); } catch { /* noop */ }
                            }, 0);
                          } catch { /* setDragImage unavailable — fall back to default ghost */ }
                          onLunchDragStart(s.id);
                        }}
                        onDragEnd={() => setDragActive(null)}
                        onContextMenu={(e) => {
                          e.preventDefault(); e.stopPropagation();
                          if (!canEditCalendar) return;
                          onLunchDelete(s.id);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (consumedLongPress(lpState, `lunch-${s.id}`)) return;
                        }}
                        {...longPressHandlers(
                          lpState,
                          `lunch-${s.id}`,
                          () => { if (canEditCalendar) onLunchDelete(s.id); },
                        )}
                        className={
                          "absolute left-1 right-1 rounded-md px-2 py-1 text-[11px] shadow-sm z-10 border border-white/40 " +
                          (canEditCalendar ? "cursor-move" : "cursor-default")
                        }
                        style={{ top: 1, height: ((le - ls) / SLOT_MIN) * slotPx - 2, backgroundColor: LUNCH_COLOR.bg, color: LUNCH_COLOR.text, touchAction: "manipulation" }}
                        data-testid={`lunch-block-${s.id}`}
                        data-instance-id={`break_${s.id}_${dateIso}`}
                        data-lunch-editable={canEditCalendar ? "true" : "false"}
                        data-lunch-date={dateIso}
                        title={canEditCalendar ? "Drag to move lunch — right-click or long-press to remove" : "Lunch break"}
                      >
                        <div className="font-medium">Lunch</div>
                        <div className="opacity-80">{daySchedule.lunch_start} – {daySchedule.lunch_end}</div>
                      </div>
                    )}
                    {presenceInCell.map((p) => {
                      const startBm = baliMinutes(p.start_iso);
                      let dur = 30;
                      try {
                        if (p.end_iso) {
                          const endBm = baliMinutes(p.end_iso);
                          if (endBm > startBm) dur = endBm - startBm;
                        }
                      } catch { /* keep default */ }
                      const height = Math.max(24, (dur / SLOT_MIN) * slotPx - 2);
                      const top = ((startBm - minute) / SLOT_MIN) * slotPx + 1;
                      return (
                        <div
                          key={`presence-${p.connection_id}`}
                          className="absolute left-1 right-1 rounded-md border-2 border-dashed border-indigo-400 bg-indigo-50/70 text-indigo-800 text-[10px] px-1.5 py-1 pointer-events-none z-20"
                          style={{ top, height, boxShadow: "0 0 0 2px rgba(255,255,255,0.6)" }}
                          data-testid={`presence-square-${p.staff_id}-${startBm}`}
                        >
                          <div className="font-semibold truncate">{p.by_name || "Someone"} is trying to book</div>
                          <div className="opacity-80 text-[9px] truncate">draft in progress…</div>
                        </div>
                      );
                    })}
                    {cellAppts.map((a) => {
                      // Iter-278 — resolve the effective processing time
                      // per tile, favouring the appointment's own value
                      // (so operator overrides still win) but falling
                      // back to the current service definition when the
                      // row was created before the buffer feature landed.
                      const svcDef = serviceById.get(a.service_id);
                      const svcProcMin = Math.max(0, Number(svcDef?.processing_time_min || 0));
                      const apptProcMin = Math.max(0, Number(a.processing_time_min || 0));
                      const procMin = Math.max(apptProcMin, svcProcMin);
                      // Iter-387 · Legacy appointments booked BEFORE the
                      // service had Requires Assistant turned on carry a
                      // stale `requires_assistant: false` on the row.
                      // Fall back to the service's current flag so the
                      // calendar renders them correctly without needing
                      // a per-appointment resave.
                      const effRequiresAssistant = !!(a.requires_assistant || svcDef?.requires_assistant);
                      // Iter-379 · Requires Assistant — when THIS staff
                      // column is for an assistant (not the primary),
                      // the tile shrinks to `processing_time_min` and
                      // the zebra overlay is suppressed (the whole
                      // assist tile IS the processing block).
                      const isAssistCol = s.id !== a.staff_id;
                      const assistShort = effRequiresAssistant && isAssistCol;
                      // Legacy row: appt.duration_min was persisted as
                      // service_time_min only, so we extend the tile
                      // height by the freshly discovered zebra tail.
                      // New rows already include procMin in duration_min
                      // so no extension is needed.
                      const legacyExtension = (apptProcMin === 0 && svcProcMin > 0) ? svcProcMin : 0;
                      const baseDur = assistShort
                        ? Math.max(5, procMin)
                        : (Number(a.duration_min || 30) + legacyExtension);
                      const dur = resizePreview[a.id] || baseDur;
                      const height = (dur / SLOT_MIN) * slotPx - 2;
                      // Processing Time (Zebra Buffer) — the buffer at
                      // the tail of every tile that internally blocks
                      // the specialist + room but is invisible to the
                      // guest. Rendered as a grey diagonal-striped
                      // section at the bottom of the tile. When neither
                      // the appointment nor the linked service carries
                      // a buffer, the overlay is omitted entirely.
                      // Suppressed on the assistant tile for Requires
                      // Assistant services (the whole tile IS the
                      // processing portion).
                      const zebraHeight = assistShort
                        ? 0
                        : (procMin > 0 ? (procMin / SLOT_MIN) * slotPx : 0);
                      // Iter-380 · Missing-Assistant alert. Tiles for a
                      // Requires Assistant service that still have no
                      // assistant assigned show an amber banner so any
                      // operator viewing ANY staff column can spot the
                      // gap. Hidden once an assistant is attached.
                      // Iter-408 · Package booking fix — package tiles
                      // mirrored from a closed invoice are BORN with
                      // `status="completed"` (orders.py:_make_appt,
                      // is_closed_order branch), so the earlier
                      // `status !== "completed"` filter was swallowing
                      // the badge on every doctor's-column package
                      // tile that still had no assistant on record.
                      // The spec's conditions are strictly:
                      //   requires_assistant === true AND
                      //   no assistant on `assist_staff_ids`.
                      // Only cancelled rows are excluded (nothing to
                      // action on a cancelled booking). This covers
                      // single-service AND package tiles uniformly
                      // across all 8 branches.
                      const assistList = Array.isArray(a.assist_staff_ids) ? a.assist_staff_ids.filter(Boolean) : [];
                      const needsAssistant = effRequiresAssistant
                        && assistList.length === 0
                        && a.status !== "cancelled";
                      const palette = colIsOff ? OFF_DUTY_APPT_COLOR : colorForAppt(a);
                      const notes = (a.notes || "").trim();
                      // Package linkage: any appointment with a link_group_id
                      // is part of a linked visit. When the operator hovers
                      // a sibling, every tile with the same id glows. The
                      // "package badge" (PackagePlus icon + tooltip line)
                      // is reserved for TRUE packages (package_id set) —
                      // ad-hoc "+ Add Another Service" bookings share a
                      // link_group_id but MUST NOT be branded as packages.
                      const linkId = a.link_group_id || null;
                      const isLinked = !!linkId;
                      const isPackage = !!a.package_id;
                      // Group scope wins: if this tile is inside the currently
                      // hovered group's expanded scope (group siblings + their
                      // package chains), it glows in the group colour AND the
                      // legacy amber link-ring is completely suppressed.
                      const inGroupScope = !!(hoveredGroupScope && hoveredGroupScope.ids.has(a.id));
                      const groupTone = inGroupScope
                        ? groupPalette[hoveredGroupScope.groupId]
                        : null;
                      // Legacy link hover — only applies when NO group hover
                      // is active anywhere on the grid. This preserves the
                      // amber ring for pure package chains (a single guest's
                      // package with no group context).
                      const isGlowing = !hoveredGroupScope && isLinked && linkId === hoveredLinkId;
                      // Show the Rp badge whenever a deposit is on file —
                      // useful even after Close Order (dark grey tile) so
                      // the operator can spot which visits arrived pre-paid.
                      // Colour, however, comes from `colorForAppt` which
                      // already returns dark grey once the visit is
                      // completed, so we no longer force-override bg here.
                      const hasDeposit = !!a.has_deposit;
                      // Iter-255 · Transportation tiles have no order, no
                      // deposit, and no payment ever — hide the PAID /
                      // UNPAID / REFUNDED pill so the driver-roster tile
                      // stays a pure driver-visible label. `showPayPill`
                      // gates every payment-status rendering below.
                      const isTransport = !!a.is_transportation;
                      const showPayPill = !isTransport;
                      const pkgName = isPackage
                        ? (packages.find((p) => p.id === a.package_id)?.name || null)
                        : null;
                      // Payment status shown at the top of every tooltip so
                      // the front desk can see paid / unpaid / refunded at a
                      // glance on hover. Also drives the "PAID" pill on the
                      // tile itself. Iter-189: treat a linked-order that's
                      // Closed or Paid as PAID even when the appointment's
                      // own `status` drifted (specialist edit on closed bill).
                      const linkedPaid = a.order_status === "completed" || a.order_status === "closed" || a.order_payment_status === "paid";
                      const rawPayStatus = (a.status === "completed" || linkedPaid)
                        ? (a.payment_refunded ? "refunded" : "paid")
                        : "unpaid";
                      // Iter-246: a no-show tile keeps its slot on the
                      // calendar and needs a distinct label so the
                      // front-desk doesn't mistake it for a real
                      // unpaid draft. "PAID" / "REFUNDED" wins over
                      // the no-show tag because those are settled
                      // financial states.
                      const isNoShow = a.status === "no_show";
                      const payStatus = (isNoShow && rawPayStatus === "unpaid") ? "no-show" : rawPayStatus;
                      const payStyle = payStatus === "paid"
                        ? "bg-emerald-500/20 text-emerald-300"
                        : payStatus === "refunded"
                          ? "bg-rose-500/20 text-rose-300"
                          : payStatus === "no-show"
                            ? "bg-amber-600/25 text-amber-200"
                            : "bg-amber-500/20 text-amber-300";
                      // Iter-246: start–end time chip for the tooltip.
                      // End time is derived at render time from
                      // start_iso + duration_min so a resized tile
                      // reflects the LIVE duration without waiting
                      // for a fresh fetch.
                      const _timeFmt = { timeZone: "Asia/Makassar", hour: "2-digit", minute: "2-digit", hour12: false };
                      const _startDt = a.start_iso ? new Date(a.start_iso) : null;
                      const _endDt = _startDt && a.duration_min
                        ? new Date(_startDt.getTime() + Number(a.duration_min) * 60000)
                        : (a.end_iso ? new Date(a.end_iso) : null);
                      const timeRange = (_startDt && _endDt)
                        ? `${_startDt.toLocaleTimeString("en-GB", _timeFmt)} – ${_endDt.toLocaleTimeString("en-GB", _timeFmt)}`
                        : "";
                      const lay = (laneLayouts[s.id] || {})[a.id] || { lane: 0, lanes: 1 };
                      // Percentage-based gap (Feb 2026): tiles occupy
                      // strictly 80% of the staff column; the remaining
                      // 20% is reserved as a permanent "new-booking"
                      // tap zone on the right. When the cluster is
                      // double-booked (lanes > 1) the 80% band is
                      // split evenly across lanes so the 20% gap on
                      // the right still holds. Percentages scale with
                      // the column, so the ratio is preserved on any
                      // viewport (phone, tablet, laptop).
                      const USABLE_COL_PCT = 80;
                      const laneWPct = USABLE_COL_PCT / lay.lanes;
                      const block = (
                        <div
                          key={a.id}
                          draggable
                          onDragStart={(e) => {
                            e.stopPropagation();
                            e.dataTransfer.setData("text/appointment-id", a.id);
                            // Iter-350 · Role-Aware Drag Split.
                            // Publish the COLUMN this drag originated from so
                            // the parent's `onGridDrop` can distinguish a
                            // primary-tile drag from an assistant-tile drag.
                            // Primary column   → dragged from `a.staff_id`.
                            // Assistant column → dragged from any id inside
                            //   `a.assist_staff_ids`.
                            // Primary drag  may update the appointment's
                            //   `staff_id` (staff swap) and/or its time.
                            // Assist drag  is STRICTLY FORBIDDEN from
                            //   overwriting `staff_id`; sideways moves only
                            //   swap the old assist for the new one in
                            //   `assist_staff_ids`. Applies globally to all
                            //   8 branches.
                            const draggedRole = s.id === a.staff_id ? "primary" : "assist";
                            e.dataTransfer.setData("text/dragged-column-staff-id", s.id);
                            e.dataTransfer.setData("text/dragged-role", draggedRole);
                            // Solid-colour drag preview: clone the tile so the browser's
                            // built-in ghost image is a full-opacity duplicate rather
                            // than the transparent default. IMPORTANT: the original
                            // tile uses `left-1 right-1` inside a narrow positioned
                            // parent — copying that to a `position:fixed` element with
                            // no parent constraint would stretch the ghost across the
                            // whole viewport (that's the "stretched to the right"
                            // artefact the user reported). Freeze the source's
                            // rendered width/height on the clone and drop the
                            // left/right rules so the ghost stays tile-sized.
                            const srcRect = e.currentTarget.getBoundingClientRect();
                            const clone = e.currentTarget.cloneNode(true);
                            clone.style.position = "fixed";
                            clone.style.left = "-1000px";
                            clone.style.top = "-1000px";
                            clone.style.right = "auto";
                            clone.style.width = `${srcRect.width}px`;
                            clone.style.height = `${srcRect.height}px`;
                            clone.style.opacity = "1";
                            clone.style.pointerEvents = "none";
                            document.body.appendChild(clone);
                            e.dataTransfer.setDragImage(clone, 20, 10);
                            // Remove the clone right after the drag image is captured.
                            setTimeout(() => { try { document.body.removeChild(clone); } catch { /* noop */ } }, 0);
                            setDragActive({ kind: "appt", service_id: a.service_id, source_id: a.id });
                          }}
                          onDragEnd={() => setDragActive(null)}
                          onClick={(e) => {
                            // Iter-268 — single click / single tap now
                            // opens the read-only View Appointment
                            // dialog. Long-press (touch) and right-
                            // click still route to the legacy context
                            // menu; we swallow the synthetic click iOS
                            // fires after `touchend` so a long-press
                            // never surprise-opens the View dialog.
                            // Iter-380 · When this tile is flagged for a
                            // missing assistant, a plain tile click
                            // short-circuits to the Edit path scrolled
                            // to the staff assignment section so the
                            // operator can resolve the gap in one tap.
                            e.preventDefault();
                            e.stopPropagation();
                            if (consumedLongPress(lpState, a.id)) return;
                            if (needsAssistant && onApptAssignAssistant) {
                              onApptAssignAssistant(a);
                              return;
                            }
                            if (onApptClick) onApptClick(a);
                            else onApptContextMenu(e, a);
                          }}
                          {...longPressHandlers(
                            lpState,
                            a.id,
                            (x, y) => onApptContextMenu(
                              { preventDefault: () => {}, stopPropagation: () => {}, clientX: x, clientY: y },
                              a,
                            ),
                          )}
                          onContextMenu={(e) => onApptContextMenu(e, a)}
                          onMouseEnter={() => {
                            if (a.group_id) enterGroup(a);
                            else if (isLinked) setHoveredLinkId(linkId);
                          }}
                          onMouseLeave={() => {
                            if (a.group_id) leaveGroup(a.group_id);
                            else if (isLinked) setHoveredLinkId((prev) => (prev === linkId ? null : prev));
                          }}
                          className={
                            "absolute rounded-md px-2 py-1 text-[11px] shadow-sm cursor-move z-10 group overflow-hidden " +
                            // Explicit transition list: box-shadow (for both
                            // the amber ring and the group glow), transform
                            // (for the subtle scale bump), and background
                            // (for status changes). 200ms ease-out keeps
                            // the fade elegant without feeling laggy.
                            "transition-[box-shadow,transform,background-color] duration-200 ease-out " +
                            // Amber package ring — ONLY when no group hover
                            // is active. This is the final CSS suppression
                            // so the group glow wins the hierarchy (spec 3).
                            (isGlowing ? "ring-2 ring-amber-400 shadow-lg shadow-amber-400/40 scale-[1.02] " : "") +
                            (inGroupScope ? "scale-[1.02] " : "")
                          }
                          style={{
                            // Sub-slot offset: for a tile that starts on a
                            // non-15-min boundary (e.g. 10:50 inside the
                            // 10:45 slot), push the top down by
                            // `(startMinute - slotMinute) / SLOT_MIN`
                            // slot-heights. Regular 10:45 tiles keep the
                            // 1px inset they always had.
                            top: ((baliMinutes(a.start_iso) - minute) / SLOT_MIN) * slotPx + 1,
                            height,
                            left: `calc(${lay.lane * laneWPct}% + 2px)`,
                            width: `calc(${laneWPct}% - 4px)`,
                            backgroundColor: palette.bg,
                            color: palette.text,
                            // Inline box-shadow beats Tailwind's ring
                            // utility (which itself sets box-shadow) so the
                            // group glow always paints on top of any link
                            // ring for tiles inside the group scope.
                            boxShadow: inGroupScope && groupTone
                              ? `0 0 24px 6px ${groupTone.ring}, 0 0 8px 2px ${groupTone.ring}`
                              : undefined,
                            // Suppress the browser's double-tap-to-zoom
                            // hesitation on touch so the tile responds
                            // instantly to a tap on iPads.
                            touchAction: "manipulation",
                          }}
                          data-testid={`appt-block-${a.id}`}
                          data-link-group-id={linkId || undefined}
                          data-group-id={a.group_id || undefined}
                          data-linked={isLinked ? "true" : undefined}
                          data-group-glowing={inGroupScope ? "true" : undefined}
                          data-glow-color={inGroupScope ? (groupTone?.key || undefined) : undefined}
                          data-has-deposit={hasDeposit ? "true" : undefined}
                          data-processing-min={procMin > 0 ? procMin : undefined}
                        >
                          {zebraHeight > 0 && (
                            <div
                              className="absolute left-0 right-0 pointer-events-none rounded-b-md"
                              style={{
                                bottom: 0,
                                height: zebraHeight,
                                backgroundImage:
                                  "repeating-linear-gradient(45deg, rgba(90,90,90,0.55) 0 6px, rgba(255,255,255,0.15) 6px 12px)",
                                backgroundColor: "rgba(180,180,180,0.55)",
                                zIndex: 1,
                              }}
                              data-testid={`appt-zebra-${a.id}`}
                              aria-hidden="true"
                              title="Processing time (staff only)"
                            />
                          )}
                          <div className="font-medium truncate flex items-center gap-1">
                            {/* Iter-225 — Online-Booking indicator. A guest
                                who booked through the customer portal
                                stamps `booking_source: "online"` on the
                                appointment (or the legacy
                                `created_by_kind: "customer"` on older rows).
                                The blue @ sits BEFORE the start-time label
                                so it never covers the HH:MM stamp. */}
                            {(a.booking_source === "online" || a.created_by_kind === "customer") && (
                              <AtSign
                                className="w-3 h-3 flex-shrink-0 text-blue-600"
                                data-testid={`appt-online-icon-${a.id}`}
                                aria-label="Booked online by guest"
                                title="Booked online by guest"
                              />
                            )}
                            <span
                              className="flex-shrink-0 text-[10px] font-bold text-black leading-tight"
                              data-testid={`appt-start-time-${a.id}`}
                              aria-label="Start time"
                            >
                              {baliLocalTime(a.start_iso)}
                            </span>
                            {a.locked_to_staff && <Lock className="w-3 h-3 flex-shrink-0" />}
                            {isPackage && <PackagePlus className="w-3 h-3 flex-shrink-0 opacity-80" />}
                            {a.group_id && (
                              <Users
                                className="w-3 h-3 flex-shrink-0 text-indigo-700"
                                data-testid={`appt-group-icon-${a.id}`}
                                aria-label="In a group"
                                title="Part of a group booking"
                              />
                            )}
                            {hasDeposit && (
                              <Banknote
                                className="w-3 h-3 flex-shrink-0 text-emerald-700"
                                data-testid={`appt-deposit-icon-${a.id}`}
                                aria-label="Deposit received"
                              />
                            )}
                            <span className="truncate">{a.customer_name || "Walk-in"}</span>
                            {a.guest_new && !a.checked_in_at && a.status !== "completed" && (
                              <span
                                className="ml-auto flex-shrink-0 rounded-full bg-amber-500 text-neutral-900 text-[8px] font-bold px-1.5 py-px leading-tight tracking-wider shadow-sm"
                                data-testid={`appt-new-badge-${a.id}`}
                                title="First-visit guest — no medical questionnaire on file yet"
                              >NEW</span>
                            )}
                            {a.status === "completed" && !isTransport && (
                              a.payment_refunded ? (
                                <span className="ml-auto flex-shrink-0 rounded-full bg-rose-600 text-white text-[8px] font-bold px-1.5 py-px leading-tight tracking-wider shadow-sm" data-testid={`appt-refunded-badge-${a.id}`}>REFUNDED</span>
                              ) : (
                                <span className="ml-auto flex-shrink-0 rounded-full bg-emerald-600 text-white text-[8px] font-bold px-1.5 py-px leading-tight tracking-wider shadow-sm" data-testid={`appt-paid-badge-${a.id}`}>PAID</span>
                              )
                            )}
                            {isTransport && a.status === "picked_up" && (
                              <span className="ml-auto flex-shrink-0 rounded-full bg-neutral-200 text-neutral-800 text-[8px] font-bold px-1.5 py-px leading-tight tracking-wider shadow-sm" data-testid={`appt-picked-up-badge-${a.id}`}>PICKED UP</span>
                            )}
                          </div>
                          <div className="opacity-90 truncate">{a.service_name}</div>
                          {/* Iter-380 · Missing-Assistant alert banner.
                              Shown on every staff column (not just the
                              doctor's) so a reception viewing anyone's
                              column catches the gap. Clicking it opens
                              the appointment Edit form scrolled to the
                              staff assignment section. Positioned
                              above the resize handle; height expands
                              with the tile so it never clips. */}
                          {needsAssistant && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onApptAssignAssistant) onApptAssignAssistant(a);
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                              className="absolute left-0 right-0 bottom-1.5 z-20 bg-amber-500 text-neutral-900 text-[9px] font-bold uppercase tracking-wider leading-tight py-[2px] px-1.5 text-center shadow-sm hover:bg-amber-400 transition-colors"
                              data-testid={`appt-assign-assistant-${a.id}`}
                              title="Assistant hasn't been assigned yet — click to assign"
                              aria-label="Assign Assistant"
                            >
                              <span aria-hidden="true">⚠</span> Assign Assistant
                            </button>
                          )}
                          {/* Bottom resize handle — native mouse events, no HTML5 DnD.
                              Snaps to 15-min increments while dragging. */}
                          <div
                            onMouseDown={(e) => onApptResizeStart(e, a)}
                            className="absolute left-0 right-0 bottom-0 h-1.5 cursor-ns-resize opacity-60 hover:opacity-100 bg-black/20"
                            data-testid={`appt-resize-${a.id}`}
                          />
                        </div>
                      );
                      // Package tiles ALWAYS get a rich tooltip (client +
                      // package + optional notes). Non-package tiles keep
                      // the notes-only tooltip as before.
                      return (
                        <Tooltip key={a.id}>
                          <TooltipTrigger asChild>{block}</TooltipTrigger>
                          <TooltipContent
                            side="right"
                            align="start"
                            className="max-w-xs bg-neutral-900 text-white whitespace-pre-wrap"
                            data-testid={`appt-tooltip-${a.id}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium truncate">{a.customer_name || "Walk-in"}</div>
                                {a.service_name && (
                                  <div className="text-xs text-white/80 mt-0.5">{a.service_name}</div>
                                )}
                              </div>
                              {timeRange && (
                                <div
                                  className="text-[10px] text-white/60 font-mono whitespace-nowrap mt-[3px] tabular-nums"
                                  data-testid={`appt-tooltip-timerange-${a.id}`}
                                >
                                  {timeRange}
                                </div>
                              )}
                            </div>
                            <div className="mt-1.5">
                              {showPayPill ? (
                                <span
                                  className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${payStyle}`}
                                  data-testid={`appt-tooltip-payment-${a.id}`}
                                >
                                  {payStatus}
                                </span>
                              ) : (
                                <span
                                  className="inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-slate-500/20 text-slate-200"
                                  data-testid={`appt-tooltip-transport-${a.id}`}
                                >
                                  {a.status === "picked_up" ? "Picked Up" : "Transportation"}
                                </span>
                              )}
                            </div>
                            {isPackage && (
                              <div className="mt-1.5 flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300">
                                <PackagePlus className="w-3 h-3" />
                                <span>Package{pkgName ? ` · ${pkgName}` : ""}</span>
                              </div>
                            )}
                            {notes && (
                              <>
                                <div className="text-[10px] uppercase tracking-wider text-white/60 mt-2 mb-0.5">Notes</div>
                                <div className="text-xs">{notes}</div>
                              </>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
